import React, { ComponentProps, MouseEvent } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Interpreter } from 'xstate';
import { useService } from '@xstate/react';
import { FormattedMessage } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';

import { AvailabilityProps, Log, SearchSummary } from 'Src/types';
import { getDefaultCustomerSort } from 'Src/utils/search-common';
import { getProvidersAtLocationUrl } from 'Src/utils/url';
import {
  LocationContext,
  LocationEvent,
  LocationStateSchema
} from 'Src/machines/location-v9/types';
import { LOGGING_EVENTS } from '../constants';
import { ContentSection, SectionHeader } from '../styles';
import messages from '../messages';
import { FindProvidersButtonLink } from './FindProvidersButtonLink';
import { ProvidersList } from './ProvidersList';
import { SortMenu } from './SortMenu';
import { Controls } from './styles';

interface BaseProvidersProps {
  config: PMCConfig;
  customerCode: string;
  locationId: string;
  log: Log;
  productName: string;
  searchSummary: SearchSummary;
  service: Interpreter<LocationContext, LocationStateSchema, LocationEvent>;
}

type ProvidersProps = BaseProvidersProps &
  ComponentProps<'section'> &
  AvailabilityProps;

export const Providers = ({
  config,
  customerCode,
  locationId,
  log,
  productName,
  searchSummary,
  service,
  appointmentInfo,
  slots,
  slotsStatus,
  purposeOptionsByProviderId,
  availabilityError,
  purposesLoading,
  fetchProviderAvailability,
  fetchPurposesByProviderId,
  ...props
}: ProvidersProps) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [state] = useService(service);

  const defaultSort = getDefaultCustomerSort(config);
  const findProvidersLink = getProvidersAtLocationUrl(id, history, defaultSort);

  const handleFindProvidersClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    log(LOGGING_EVENTS.CLICK_FIND_PROVIDERS);
    history.push(findProvidersLink);
  };

  const { totalProviders = 0 } = state.context;

  return (
    <ContentSection
      data-testid="location-providers"
      aria-labelledby="location-providers-title"
      {...props}
    >
      <FormattedMessage
        {...messages.providersTitle}
        values={{ count: totalProviders }}
      >
        {(text) => (
          <SectionHeader
            id="location-providers-title"
            data-testid="location-providers-title"
          >
            {text}
          </SectionHeader>
        )}
      </FormattedMessage>

      <Controls>
        <FormattedMessage {...messages.findProvidersAtLocationButtonLabel}>
          {(text) => (
            <Link
              to={findProvidersLink}
              onClick={handleFindProvidersClick}
              component={FindProvidersButtonLink}
            >
              {text}
            </Link>
          )}
        </FormattedMessage>
        {totalProviders > 0 && (
          <SortMenu config={config} service={service} log={log} />
        )}
      </Controls>

      {totalProviders > 0 && (
        <ProvidersList
          config={config}
          customerCode={customerCode}
          locationId={locationId}
          log={log}
          productName={productName}
          searchSummary={searchSummary}
          service={service}
          appointmentInfo={appointmentInfo}
          slots={slots}
          slotsStatus={slotsStatus}
          purposeOptionsByProviderId={purposeOptionsByProviderId}
          availabilityError={availabilityError}
          purposesLoading={purposesLoading}
          fetchProviderAvailability={fetchProviderAvailability}
          fetchPurposesByProviderId={fetchPurposesByProviderId}
        />
      )}
    </ContentSection>
  );
};
